// src/components/settings/shared/FormComponents.jsx
import React from 'react';
import { AlertCircle, Upload, X } from 'lucide-react';

export const FormField = ({ 
  label, 
  required = false, 
  error, 
  description, 
  children,
  className = ""
}) => (
  <div className={`space-y-2 ${className}`}>
    <label className="block text-sm font-medium text-gray-700">
      {label}
      {required && <span className="text-red-500 ml-1">*</span>}
    </label>
    {description && (
      <p className="text-sm text-gray-500">{description}</p>
    )}
    {children}
    {error && (
      <div className="flex items-center gap-2 text-red-600 text-sm">
        <AlertCircle className="h-4 w-4" />
        {error}
      </div>
    )}
  </div>
);

export const TextInput = ({ value, onChange, placeholder, error, ...props }) => (
  <input
    type="text"
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
      error ? 'border-red-300' : 'border-gray-300'
    }`}
    {...props}
  />
);

export const SelectInput = ({ value, onChange, options, error, placeholder, ...props }) => (
  <select
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
      error ? 'border-red-300' : 'border-gray-300'
    }`}
    {...props}
  >
    {placeholder && <option value="">{placeholder}</option>}
    {options.map(option => (
      <option key={option.value} value={option.value}>
        {option.label}
      </option>
    ))}
  </select>
);

export const TextArea = ({ value, onChange, placeholder, rows = 3, error, ...props }) => (
  <textarea
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    rows={rows}
    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none ${
      error ? 'border-red-300' : 'border-gray-300'
    }`}
    {...props}
  />
);

export const Toggle = ({ checked, onChange, label, description }) => (
  <div className="flex items-start justify-between">
    <div className="flex-1">
      <div className="text-sm font-medium text-gray-700">{label}</div>
      {description && <div className="text-sm text-gray-500">{description}</div>}
    </div>
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
        checked ? 'bg-blue-600' : 'bg-gray-200'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  </div>
);

export const TabGroup = ({ tabs, activeTab, onChange, vertical = false }) => (
  <div className={`flex ${vertical ? 'flex-col' : ''} gap-1`}>
    {tabs.map(tab => (
      <button
        key={tab.id}
        onClick={() => onChange(tab.id)}
        className={`px-3 py-2 rounded-lg transition-colors text-sm font-medium ${
          activeTab === tab.id 
            ? 'bg-blue-100 text-blue-700 border border-blue-200' 
            : 'hover:bg-gray-100 text-gray-600 border border-transparent'
        }`}
      >
        {tab.label}
      </button>
    ))}
  </div>
);

// Alternative horizontal tab navigation (more like browser tabs)
export const TabNavigation = ({ tabs, activeTab, onChange }) => (
  <div className="border-b border-gray-200">
    <nav className="-mb-px flex space-x-8">
      {tabs.map(tab => (
        <button
          key={tab.id}
          onClick={() => onChange(tab.id)}
          className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
            activeTab === tab.id
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          {tab.label}
        </button>
      ))}
    </nav>
  </div>
);